import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import withName from "../../hoc/withName";
import { Outlet } from "react-router";
import { useTranslation } from "react-i18next";
import RestAPI from "@aws-amplify/api-rest";
import { FarmContext } from "../../contexts/FarmContext";
import LoadingDialog from "../Utils/LoadingDialog";
import { toast } from "react-toastify";
import { Common } from "./Services/Common";


const Marketplace = () => {
    /**
     * @type {[typeof availableConnections, any]}
     */
    const [fetchedConnections, setConnections] = useState();
    const [farmConnections, setFarmConnections] = useState();
    const [dataLoading, setDataLoading] = useState(false);
    const [paises, setPaises] = useState();
    const [{ dataFarm }, dispatch] = useContext(FarmContext);
    const canAutorizarApi = dispatch({ type: "get", path: "canAutorizarApi" });
    const acceso_limitado = dispatch({ type: "get", path: "accesoLimitado" });
    const [activeTab, setActiveTab] = useState(0);
    const [filterData, setFilterData] = useState({
        searchText: "",
        selectedTypes: [],
        selectedCategories: [],
        selectedSort: "sortDate",
        categoriesOptions: [],
        typesOptions: [],
        sortOptions: []
    });
    const { t, i18n } = useTranslation();
    const [reFetch, setReFetch] = useState(false);
    const [firstFetch, setFirstFetch] = useState(true);

    const getData = useCallback(async () => {
        try {
            // await new Promise(resolve => setTimeout(resolve, 2000))
            await Promise.all([
                // Promise.resolve(availableConnections).then((res) => setConnections(res)),
                // Promise.resolve(connected).then((res) => setFarmConnections(res)),
                RestAPI.get("apiAdminDropControl", `/marketplace/${dataFarm.campo_id}`)
                    .then(res => {
                        setConnections(res);
                    }),
                RestAPI.get("apiAdminDropControl", `/farms/${dataFarm.campo_id}/paises`)
                    .then(res => {
                        setPaises(res);
                    }),
                RestAPI.get("apiAdminDropControl", `/marketplace/${dataFarm.campo_id}/connected`)
                    .then(res => {
                        setFarmConnections(res);
                    })
            ]);
        } catch (error) {
            toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
        } finally {
            setDataLoading(false);
            document.getElementById("containerMarketplace")?.scrollTo(0, 0);
        }
    }, [dataFarm.campo_id, t]);

    useEffect(() => {
        if (firstFetch){
            setDataLoading(true);
            getData();
            setFirstFetch(false);
        }
    }, [getData, firstFetch]);

    useEffect(() => {
        if (reFetch) {
            setDataLoading(true);
            setActiveTab(1);
            getData();
            setReFetch(false);
        }
    }, [getData, reFetch]);

    const connectionsData = useMemo(() => {
        if (!fetchedConnections || !farmConnections) return [];
        let transformedData = fetchedConnections.map(connection => {
            const data = connection;
            // set child farm connections
            const alreadyConnected = farmConnections.filter(c => c.marketplace_servicio_id === connection.marketplace_servicio_id);
            connection.childs = alreadyConnected.map(c =>  Common.resolveConnectionIdentifier(c, t));
            // set disabled state
            connection.disabled_state = { disabled: false };
            const onlyOneAllowed = ["API","FEATURE","API_SDB","REQUEST"].includes(connection?.tipo_formulario);
            if (onlyOneAllowed && connection?.childs?.length > 0){
                connection.disabled_state = { disabled: true, reason: t("marketplace.disabledReason.onlyOne") };
            } else if (!connection?.habilitado_pais) {
                connection.disabled_state = { disabled: true, reason: t("marketplace.disabledReason.invalidCountry") };
            } else if (!connection?.habilitado_plan) {
                connection.disabled_state = { disabled: true, reason: t("marketplace.disabledReason.invalidPlan") };
            }
            return {
                ...data,
                nombre_servicio: typeof data.nombre_servicio === "string" ?
                    data.nombre_servicio : (data.nombre_servicio[i18n.language] ?? data.nombre_servicio["en"] ?? ""),
                descripcion: data.descripcion?.[i18n.language] ?? data.descripcion?.["en"] ?? "",
                paises: data.countries_locale?.map(c => c[i18n.language] ?? c["en"]).join(", ") || "",
            };
        });
        if (!acceso_limitado && canAutorizarApi){
            return transformedData.sort((a,b) => a.nombre_servicio.localeCompare(b.nombre_servicio));
        } else {
            transformedData = transformedData.map((c) => ({ ...c, habilitado_pais: false, disabled_state: { disabled: true, reason: t("forms.toastError401") } }));
            return transformedData.sort((a,b) => a.nombre_servicio.localeCompare(b.nombre_servicio));
        }
    }, [acceso_limitado, canAutorizarApi, farmConnections, fetchedConnections, i18n.language, t]);

    const farmConnectionsData = useMemo(() => {
        if (!farmConnections) return [];
        const transformedData = farmConnections.map(data => {
            if (data.connection_data?.fecha_solicitud){
                data.connection_data.created_at = data.connection_data.fecha_solicitud;
            } else if (data.connection_data?.config?.created_at){
                data.connection_data.created_at = data.connection_data.config.created_at;
            }
            let nombre_servicio = typeof data.nombre_servicio === "string" ?
                data.nombre_servicio : (data.nombre_servicio[i18n.language] ?? data.nombre_servicio["en"] ?? "");
            if (data.tipo_formulario === "EXT_DAVIS"){
                nombre_servicio += ` (V${data.connection_data?.config?.version ?? 2})`;
            }
            return {
                ...data,
                nombre_servicio,
                descripcion: data.descripcion?.[i18n.language] ?? data.descripcion?.["en"] ?? "",
                paises: data.countries_locale?.map(c => c[i18n.language] ?? c["en"]).join(", ") ?? "",
                tipo_i18n: t(`marketplace.products.${data.tipo}`)
            };
        });
        return transformedData.sort((a,b) => a.nombre_servicio.localeCompare(b.nombre_servicio));
    }, [farmConnections, i18n.language, t]);

    const typesOptions = useMemo(() => {
        const farmTypes = Array.from(new Set(connectionsData?.map(c => c.tipo)));
        const allTypes = [
            { label: t("marketplace.products.apiWiseconn"), value: "apiWiseconn" },
            { label: t("marketplace.products.externalData"), value: "externalData" },
            { label: t("marketplace.products.externalImages"), value: "externalImages" },
            { label: t("marketplace.products.feature"), value: "feature" },
            { label: t("marketplace.products.request"), value: "request" },
            { label: t("marketplace.products.other"), value: "other" },
        ];
        return allTypes.filter(c => farmTypes.includes(c.value));
    } , [connectionsData, t]);

    const categoriesOptions = useMemo(() => {
        const farmCategories = Array.from(new Set(connectionsData?.flatMap(c => c.categorias)));
        const allCategories = [
            { label: t("marketplace.categories.imagery"), value: "imagery" },
            { label: t("marketplace.categories.irrigation"), value: "irrigation" },
            { label: t("marketplace.categories.weather"), value: "weather" },
            { label: t("marketplace.categories.soilMoisture"), value: "soilMoisture" },
            { label: t("marketplace.categories.other"), value: "other" }
        ];
        return allCategories.filter(c => farmCategories.includes(c.value));
    }, [connectionsData, t]);

    const sortOptions = useMemo(() => [
        { label: t("marketplace.companyName"), value: "sortCompany" },
        { label: t("marketplace.productName"), value: "sortProductName" },
        { label: t("marketplace.enablingDate"), value: "sortDate" },
        { label: t("marketplace.product"), value: "sortProduct" },
    ], [t]);

    useEffect(() => {
        setFilterData( prev => {
            return {
                ...prev,
                selectedTypes: typesOptions.map(e => e.value),
                selectedCategories: categoriesOptions.map(e => e.value),
                selectedSort: "sortDate",
                categoriesOptions,
                sortOptions,
                typesOptions
            };
        });
    }, [typesOptions, categoriesOptions, setFilterData, sortOptions]);

    return (
        <div id="containerMarketplace" className='row-container marketplace'>
            <div className='col-xl-12 col-lg-12 col-sm-12'>
                <div id="cardMarketplace" className="card">
                    {dataLoading && <LoadingDialog appendTo="self" />}
                    <div className="card-body">
                        <Outlet key="marketplace" context={{
                            farmId: dataFarm?.campo_id ?? null,
                            paises: paises ?? [],
                            availableConnections: connectionsData ?? [],
                            setConnections,
                            filterData,
                            setFilterData,
                            farmConnections: farmConnectionsData ?? [],
                            setFarmConnections,
                            activeTab,
                            setActiveTab,
                            setReFetch,
                            setDataLoading,
                            canAutorizarApi
                        }}
                        />
                    </div>
                </div>
            </div>
        </div>


    );
};

export default withName(Marketplace, "MarketplaceFrag");

